import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import CreateMoleculeModal from 'chemistry/molecules/CreateMoleculeModal';
import type {CreatedParentMolecule} from 'chemistry/molecules/MoleculeDAO';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import StoichiometryTableEmbeddedChemicalSketcher from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableEmbeddedChemicalSketcher';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import GroupTitleBar from 'notebook/notes/groups/GroupTitleBar';
import {getStoichiometryGroupAdapter} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import {
  GroupActionFunctionParameters,
  StructuredTableSpreadsheetProps,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {reduceGroupUpdateNotePartItem} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import {getContainedTableSearchResultProps} from 'notebook/notes/groups/util/getContainedTableSearchResultProps';
import groupKindToIcon from 'notebook/notes/groups/util/groupKindToIcon';
import maybeAsyncUpdateContainedNotePart from 'notebook/notes/groups/util/maybeAsyncUpdateContainedNotePart';
import {makeTableItemPropsByName} from 'notebook/notes/groups/util/NoteLinkedGroupViewSpreadsheetProps';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {generateNoteLinkedObjectCollapseId} from 'notebook/notes/util/generateNLCollapseId';
import {
  ActionFunctionContext,
  useActionFunctionContext,
} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import StructuredTableFormOrView from 'notebook/structured-tables/StructuredTableFormOrView';
import {NoteLinkedStructuredTable} from 'notebook/structured-tables/StructuredTablePlugin';
import {useNotebookItemCollapse} from 'notebook/tables/collapse/useNotebookItemCollapse';
import {type TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {NoteLinkedFile, NotePart} from 'notebook/util/NoteUtilConstants';
import {GRAY} from 'ui-library/Colors';
import legacyRunModal from 'ui-library/legacyRunModal';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import memoizeLastCall from 'util/memoizeLastCall';

import attachUnrecognizedMoleculeResolutionActions, {
  type InitialChemicalStructureForUnrecognizedMolecule,
} from './attachUnrecognizedMoleculeResolutionActions';
import {
  makeEmptyReactionSchemeFile,
  StoichiometryCreateGroupExtras,
  StoichiometryGroup,
  StoichiometryGroupMetadata,
} from './StoichiometryNoteLinkedGroupAdapter';
import {StoichiometryStructuredTable} from './StoichiometryStructuredTableAdapter';
import {updateStoichiometryTableWithCreatedMoleculeAndParent} from './updateStoichiometryTableWithCreatedMolecule';

export interface StoichiometryGroupViewProps extends Partial<MediocreSearchResultProps> {
  notePartCid: string;
  group: StoichiometryGroup;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  spreadsheetProps: StructuredTableSpreadsheetProps;
}

interface ActionFunctionContextProps {
  actionFunctionContext: ActionFunctionContext;
}

function GroupActionButtons<TGroupMetadata extends {}, TCreateGroupExtras extends {}>(
  props: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
): JSX.Element {
  const actionFunctions = props.noteLinkedGroupAdapter.getActionFunctions();

  // When collapsed, only show the primary action function.
  return (
    <>
      {!props.isCollapsed &&
        actionFunctions.secondary?.map((actionFunction, index) => (
          <React.Fragment key={index}>{actionFunction.renderToolbarButton(props)}</React.Fragment>
        ))}
      {!props.isCollapsed && actionFunctions.overflow?.length ? (
        <OverflowMenu
          options={actionFunctions.overflow
            .map((actionFunction) => actionFunction.getOverflowMenuItem(props))
            .filter((option) => option != null)}
        />
      ) : null}
      {actionFunctions.primary?.renderToolbarButton(props)}
    </>
  );
}

class StoichiometryGroupInnerView extends React.Component<
  StoichiometryGroupViewProps &
    ActionFunctionContextProps & {
      isCollapsed: boolean;
      onToggleCollapsed: () => void;
    }
> {
  private emptyReactionSchemeFile: NoteLinkedFile | null = null;
  private emptyReactionSchemeFileName: string | null = null;

  private getOrCreateEmptyReactionSchemeFile(name: string): NoteLinkedFile {
    if (this.emptyReactionSchemeFile == null || this.emptyReactionSchemeFileName !== name) {
      this.emptyReactionSchemeFile = makeEmptyReactionSchemeFile(name);
      this.emptyReactionSchemeFileName = name;
    }
    return this.emptyReactionSchemeFile;
  }

  handleDeleteGroup = (): void => {
    const {editor, notePartCid} = this.props;
    const parentIndex = editor.getIndex(notePartCid);
    assert(parentIndex !== -1, `Could not find note part with cid ${notePartCid}`);
    editor.itemDeleteHandler(parentIndex);
  };

  handleUpdate = (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable): void => {
    const {group, editor, editorProps, notePartCid} = this.props;
    const adapter = getStoichiometryGroupAdapter();
    const reactionSchemePart = adapter.getReactionSchemeNotePart(group);
    const tablePart = adapter.getStoichiometryTableNotePart(group);
    // The NoteLinkedFile's cid is the server-assigned uuid from process_reaction_components
    // (set in handleReactionSketcherSubmit). Register the cid → id mapping so the patcher
    // can find the existing NoteLinkedFile by id on save.
    if (updatedFile.cid != null) {
      editorProps.registerObjects({[updatedFile.cid]: updatedFile.cid});
    }
    let updatedGroup: StoichiometryGroup = group;
    if (reactionSchemePart != null) {
      updatedGroup = reduceGroupUpdateNotePartItem(updatedGroup, reactionSchemePart, updatedFile);
    } else {
      // No NoteLinkedFile part exists yet — add one to the group.
      const newReactionSchemePart = reactUpdate(makeDefaultItem(ModelTypeConstants.NOTE_LINKED_OBJECT), {
        item: {$set: updatedFile},
      });
      updatedGroup = reactUpdate(updatedGroup, {
        containedNoteParts: {$unshift: [newReactionSchemePart]},
      });
    }
    updatedGroup = reduceGroupUpdateNotePartItem(updatedGroup, tablePart, updatedTable);
    editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: updatedGroup});
  };

  getUnrecognizedMoleculeResolutionAction =
    (
      canonicalSmiles: string,
      initialChemicalStructure: InitialChemicalStructureForUnrecognizedMolecule,
      initialName?: string
    ) =>
    () => {
      legacyRunModal(CreateMoleculeModal, {
        initialFolderId: null,
        initialChemicalStructure,
        initialName,
        onMoleculeCreated: async (
          moleculeId: string,
          moleculeName: string,
          parentMolecule?: CreatedParentMolecule
        ) => {
          const {group, editor, notePartCid} = this.props;
          const adapter = getStoichiometryGroupAdapter();
          const tablePart = adapter.getStoichiometryTableNotePart(group);
          const updatedTable = updateStoichiometryTableWithCreatedMoleculeAndParent(
            tablePart.item,
            {canonicalSmiles, id: moleculeId, name: moleculeName},
            parentMolecule
          );
          if (updatedTable == null) {
            return;
          }
          const updatedContainedNotePart = {...tablePart, item: updatedTable};
          await maybeAsyncUpdateContainedNotePart(group, editor, notePartCid, updatedContainedNotePart);
        },
      });
    };

  _getStableTableItemProps = memoizeLastCall(makeTableItemPropsByName);

  _enrichResolutionActions = memoizeLastCall(attachUnrecognizedMoleculeResolutionActions);

  override render(): JSX.Element {
    const {
      group,
      editor,
      editorProps,
      notePartCid,
      spreadsheetProps,
      actionFunctionContext,
      isCollapsed,
      onToggleCollapsed,
    } = this.props;
    const adapter = getStoichiometryGroupAdapter();
    const {evaluatedTableMap, tableItemPropsByName} = this._getStableTableItemProps(
      group,
      editor,
      spreadsheetProps,
      editor.props.cellColors,
      editor.props.editingTable,
      editor.props.computedTables
    );

    const reactionSchemePart = adapter.getReactionSchemeNotePart(group);
    const tablePart = adapter.getStoichiometryTableNotePart(group);
    const tablePartIndex = group.containedNoteParts.indexOf(tablePart);
    const structuredTable = tablePart.item;
    const tableAdapter = getStructuredTableAdapters()[structuredTable.kind];
    const tableData = tableAdapter.toTable(structuredTable);
    const computedTable = evaluatedTableMap[tableData.name];
    const tableItemProps = tableItemPropsByName[tableData.name];

    const actionFunctionParameters: GroupActionFunctionParameters<
      StoichiometryGroupMetadata,
      StoichiometryCreateGroupExtras
    > = {
      entryOrTemplateId: editorProps.fileId,
      noteLinkedGroup: group,
      noteLinkedGroupAdapter: adapter,
      locked: editorProps.locked,
      isCollapsed,
      evaluatedTableMap,
      // Stoichiometry groups have no cross-table invariants that the new resync hook detects;
      // their adapter inherits the no-op default from GenericNoteLinkedGroupAdapter.
      groupResyncRequired: false,
      actionFunctionContext,
      getItemWithUpdatedContent: editor.getItemWithUpdatedContent,
      getIdFromCidFn: editorProps.getIdFromCid,
      updateGroup: (noteLinkedGroup) => {
        editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: noteLinkedGroup});
      },
      insertAfterGroup: (noteParts, cidIdMap) => {
        editor.props.registerObjects(cidIdMap);
        editor.itemInsertHandler(editor.getIndex(notePartCid), noteParts);
      },
      selectorsById: {},
    };

    const isExpanded = !isCollapsed || editorProps.viewer;
    const printableContainerClass = editorProps.viewer ? 'printable-container-scaleForPrint' : '';

    if (BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS) {
      this._enrichResolutionActions(
        computedTable,
        structuredTable,
        this.getUnrecognizedMoleculeResolutionAction
      );
    }

    return (
      <SelectableNotePart indentation={0} className={printableContainerClass}>
        <div className={css(styles.groupWrapper)}>
          <GroupTitleBar
            groupTypeLabel={adapter.getDisplayName(group)}
            groupName={group.name}
            icon={groupKindToIcon(group.kind)}
            rightHeaderControls={<GroupActionButtons {...actionFunctionParameters} />}
            isCollapsed={isCollapsed}
            onToggleCollapsed={onToggleCollapsed}
            viewer={editorProps.viewer}
          />
          {isExpanded && (
            <div
              role="presentation"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onMouseOver={(e) => {
                e.stopPropagation();
              }}
            >
              <StoichiometryTableEmbeddedChemicalSketcher
                reactionSchemeFile={
                  reactionSchemePart?.item ?? this.getOrCreateEmptyReactionSchemeFile(group.name)
                }
                onUpdate={this.handleUpdate}
                structuredTable={structuredTable}
                computedTable={computedTable ?? []}
                locked={editorProps.locked}
                viewer={editorProps.viewer}
              />
              {tableItemProps != null && (
                <StructuredTableFormOrView
                  editor={editor}
                  editorProps={editorProps}
                  index={tablePartIndex}
                  notePart={tablePart as unknown as NotePart & {item: NoteLinkedStructuredTable}}
                  onUpdateContent={async (
                    containedNotePartIndex: number,
                    content: object,
                    _selectors?: never,
                    tableOp?: TableOpDiffMap
                  ): Promise<void> => {
                    const oldContainedNotePart = group.containedNoteParts[containedNotePartIndex];
                    const updatedContainedNotePart = editor.getItemWithUpdatedContent(
                      oldContainedNotePart,
                      content
                    );
                    await maybeAsyncUpdateContainedNotePart(
                      group,
                      editor,
                      notePartCid,
                      updatedContainedNotePart,
                      tableOp
                    );
                  }}
                  isInGroup={true}
                  deleteHandler={this.handleDeleteGroup}
                  itemProps={tableItemProps}
                  adapter={tableAdapter}
                  overrideDefaultTableProps={{
                    disableMaximizedTables: true,
                    isCollapsible: false,
                  }}
                  {...getContainedTableSearchResultProps(this.props, tablePart.cid)}
                />
              )}
            </div>
          )}
        </div>
      </SelectableNotePart>
    );
  }
}

export function StoichiometryGroupView(props: StoichiometryGroupViewProps): JSX.Element {
  const actionFunctionContext = useActionFunctionContext();

  const {isCollapsed, onToggleCollapsed} = useNotebookItemCollapse(
    generateNoteLinkedObjectCollapseId(props.editorProps.fileId, props.group.provenanceUuid)
  );

  return (
    <StoichiometryGroupInnerView
      {...props}
      actionFunctionContext={actionFunctionContext}
      isCollapsed={isCollapsed}
      onToggleCollapsed={onToggleCollapsed}
    />
  );
}

const styles = StyleSheet.create({
  groupWrapper: {
    backgroundColor: 'white',
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    display: 'inline-block',
    maxWidth: '100%',
    position: 'relative',
    width: '100%',
  },
});
